import React, { createContext, useState, useRef, useEffect } from 'react';
import { Configuration } from '../data/configuration.js';
import { CameraController, CameraViews } from '../camera-controller.js';
import { PaintConfigurator } from '../configurator/paint-configurator.js';
import { WheelConfigurator } from '../configurator/wheel-configurator.js';
import { InteriorConfigurator } from '../configurator/interior-configurator.js';
import { ExteriorConfigurator } from '../configurator/exterior-configurator.js';
import { AccessoriesConfigurator } from '../configurator/accessories-configurator.js';
import { SubModelConfigurator } from '../configurator/sub-model-configurator.js';
import { HomeView } from './home-view.js';
import { ModelUnavailableView } from './model-unavailable-view.js';
import { LoadConfigurationView } from './load-configuration-view.js';
import { PaintworkView } from './paintwork-view.tsx';
import { WheelsView } from './wheels-view.js';
import { InteriorView } from './interior/interior-view.tsx';
import { ExteriorView } from './exterior/exterior-view.tsx';
import { AccessoriesView } from './accessories/accessories-view.tsx';
import { SummaryView } from './summary/summary-view.tsx';
import { SendToDealerView } from './summary/save-configuration/send-to-dealer-view.tsx';
import { ImportantInformationView } from './important-information/important-information-view.tsx';
import { VehicleMenu } from './vehicle-view.js';
import Modal from './ui/modal.tsx';
import { CloudData } from '../data/cloud-data.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

export enum ViewStates {
	Home,
	LoadConfiguration,
	Paintwork,
	Wheels,
	Interior,
	Exterior,
	Accessories,
	Summary,
	SendToDealer,
	ImportantInformation
}

interface AppProps {
	cameraController: CameraController;
	paintConfigurator: PaintConfigurator;
	wheelConfigurator: WheelConfigurator;
	interiorConfigurator: InteriorConfigurator;
	exteriorConfigurator: ExteriorConfigurator;
	accessoriesConfigurator: AccessoriesConfigurator;
	subModelConfigurator: SubModelConfigurator;
	isMobile: boolean;
}

export type GlobalUserDetails = {
	firstName: string,
	lastName: string,
	email: string,
}

interface AppContextType {
	viewState: ViewStates;
	handleChangeView: (state: ViewStates) => void;
	cameraController: CameraController;
	restartConfiguration: () => void;
	exteriorConfigurator: ExteriorConfigurator;
	globalUserDetails: GlobalUserDetails;
	updateGlobalUserDetails: (key: string, value: string) => void;
	hoodUp: boolean;
	toggleHood: () => void;
	hardTopVisible: boolean;
	toggleHardTop: () => void;
	windowsUp: boolean;
	setWindowsUp: (windowsUp: boolean) => void;
	setHardTopVisible: (hoodUp: boolean) => void;
	lowerRoofAndDoors: () => void;
	isDrawerOpen: boolean;
	setIsDrawerOpen: (isOpen: boolean) => void;
	isMobile: boolean;
	applyConfiguration: () => void;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function App(props: AppProps) {

	// states.
	const [viewState, setViewState] = useState<ViewStates>(ViewStates.Home);
	const [globalUserDetails, setGlobalUserDetails] = useState<GlobalUserDetails>({
		firstName: '', lastName: '', email: ''
	});
	const [hoodUp, setHoodUp] = useState(false);
	const [hardTopVisible, setHardTopVisible] = useState(false);
	const [windowsUp, setWindowsUp] = useState(true);
	const [isDrawerOpen, setIsDrawerOpen] = useState(props.isMobile);

	// the model availability message is shown ahead of the home view, and stays dismissed for the rest of the session.
	const [modelUnavailableDismissed, setModelUnavailableDismissed] = useState(false);
	const previousViewState = useRef<ViewStates>(ViewStates.Home);

	// mirror of previousViewState for values read during render (refs may not be read during render).
	const [previousViewForRender, setPreviousViewForRender] = useState<number>(ViewStates.Home);

	// applies the current configuration to the vehicle.
	const applyConfiguration = () => {

		// sub-model.
		props.subModelConfigurator.selectSubModel();

		// paint. a bespoke colour is carried as its paint type's option id, which selectBodyPaint ignores, so the colour is applied separately.
		props.paintConfigurator.selectBodyPaint(Configuration.bodyPaintColour);
		if (Configuration.isBespokePaintColour(Configuration.bodyPaintColour) && Configuration.bespokePaintColour) {
			props.paintConfigurator.selectBespokeBodyPaint(Configuration.bespokePaintColour, CloudData.getPaintTypeForId(Configuration.bodyPaintColour));
		}

		// wheels.
		props.wheelConfigurator.selectWheelStyle(Configuration.wheelStyle);
		props.wheelConfigurator.selectFrontBrakeCaliper(Configuration.frontBrakeCaliperOption);

		// interior.
		props.interiorConfigurator.selectGearStickOption(Configuration.gearStickOption);
		props.interiorConfigurator.selectSeatStitchingStyle(Configuration.seatStitchingOption); // n.b. stitching needs to be set before leather style.
		props.interiorConfigurator.selectLeatherStyle(Configuration.leatherStyle, Configuration.upholsteryContrastOption);
		props.interiorConfigurator.selectCarpetColour(Configuration.carpetColour);
		props.interiorConfigurator.selectDashboardOption(Configuration.dashboardOption);
		props.interiorConfigurator.selectSteeringWheelOption(Configuration.steeringWheelOption);
		props.interiorConfigurator.selectSteeringColumnOption(Configuration.steeringColumnOption);
		props.interiorConfigurator.selectCentreConsoleOption(Configuration.centreConsoleOption);
		props.interiorConfigurator.selectTunnelSidesStyle();
		props.interiorConfigurator.selectSeatPleatingStyle(Configuration.seatPleatingOption);
		props.interiorConfigurator.selectSeatBackStyle(Configuration.seatBackStyle);
		props.interiorConfigurator.selectEmbroideryOption(Configuration.headrestEmbroideryOption);
		props.interiorConfigurator.selectSteeringWheelOption(Configuration.steeringWheelOption);
		
		// exterior.
		props.exteriorConfigurator.selectHoodStyle(Configuration.hoodStyleOption);
		props.exteriorConfigurator.selectHardTop(Configuration.hardTopOption, hoodUp);
		props.exteriorConfigurator.selectAccentPackStyle(Configuration.accentPackOption);
		props.exteriorConfigurator.selectDoorGraphicOption(Configuration.doorGraphicOption);
		props.exteriorConfigurator.selectStripeGraphicOption(Configuration.stripeGraphicOption);
		props.exteriorConfigurator.selectSillGraphicOption(Configuration.sillGraphicOption);

		// accessories.
		Array.from(Configuration.accessoryStorageOptions).map((id) => {
			props.accessoriesConfigurator.selectStorageAccessory(id);
		});
		Array.from(Configuration.accessoryEquipmentOptions).map((id) => {
			props.accessoriesConfigurator.selectEquipmentAccessory(id);
		});
		Array.from(Configuration.accessoryAftermarketOptions).map((id) => {
			props.accessoriesConfigurator.selectAftermarketAccessory(id);
		});
		props.accessoriesConfigurator.selectAudioAccessory(Configuration.audioOption);
	};

	// handlers.

	const handleConfigurationDataLoaded = () => {

		// apply the configuration to the vehicle.
		applyConfiguration();

		// if the prev page showed the vehicle then return to it.
		if (
			previousViewState.current !== ViewStates.Home &&
				previousViewState.current !== ViewStates.LoadConfiguration &&
				previousViewState.current !== ViewStates.Summary &&
				previousViewState.current !== ViewStates.SendToDealer &&
				previousViewState.current !== ViewStates.ImportantInformation
		) {
			// show the previous view state.
			setViewState(previousViewState.current);
		} else {
			// show the body paint view.
			setViewState(ViewStates.Paintwork);
		}

		showCanvas(true);
	};

	const handleChangeView = (state: ViewStates) => {

		// store the previous view state, so we can return to it or display specific UI based on the previous screen.
		previousViewState.current = viewState;

		// mirror into state, so views can react to the previous screen during render.
		setPreviousViewForRender(previousViewState.current);

		setViewState(state);

		// show the canvas on all pages, apart from the following:
		if (state === ViewStates.Home ||
			state === ViewStates.LoadConfiguration ||
			state === ViewStates.Summary ||
			state === ViewStates.SendToDealer ||
			state === ViewStates.ImportantInformation) {
			showCanvas(false);
		}
		else {
			showCanvas(true);
		}
	};

	/*
		clears every selection, re-prices, resets the vehicle and returns to the home screen. this lives here
		rather than in a view because it needs both applyConfiguration and the accessories configurator, and is
		reached from the restart control on the vehicle panel (see RestartOptions).
	*/
	const restartConfiguration = () => {
		Configuration.resetConfiguration();
		Configuration.updateCosts();
		props.accessoriesConfigurator.resetAccessories();
		applyConfiguration();
		handleChangeView(ViewStates.Home);
	};

	const handleStartConfigurationButtonClicked = () => {

		if (!props.isMobile) {
			props.cameraController.updateDesktopCameraFov();
		}

		// apply the (default) configuration to the vehicle.
		applyConfiguration();

		// if the auto-start debug option has been set, start on the specified view.
		if (Configuration.debugOptions.autoStart) {
			handleChangeView(Configuration.debugOptions.autoStartView);
		}

		// otherwise, show the paintwork view.
		else {
			handleChangeView(ViewStates.Paintwork);
		}
	};

	// returns the relevant view, based on the current state.
	const currentView = () => {
		switch (viewState) {
			case ViewStates.Home:

				// the supersport 400 is not sold in the US, so present the availability message ahead of the home view.
				// dismissing it falls through to the home view, where the region selector can correct a wrongly detected region.
				if (Configuration.isSupersport400ModelInUsRegion() && !modelUnavailableDismissed) {
					return (
						<ModelUnavailableView
							onContinueBrowsingClicked={() => {
								setModelUnavailableDismissed(true);
							}}
						/>
					);
				}

				// set the camera to the accent pack viewpoint, so we get a nice transition when selecting the subsequent configuration pages.
				props.cameraController.changeCameraView(CameraViews.AccentPackView, undefined, true);

				// present the home view.
				return (
					<HomeView
						onStartConfigurationButtonClicked={handleStartConfigurationButtonClicked}
						onLoadConfigurationButtonClicked={() => {
							handleChangeView(ViewStates.LoadConfiguration);
						}}
					/>
				);

			case ViewStates.LoadConfiguration:

				// present the load configuration view.
				return (
					<LoadConfigurationView
						onConfigurationDataLoaded={handleConfigurationDataLoaded}
						onBackButtonClicked={() => {
							handleChangeView(previousViewState.current as ViewStates);
						}}
					/>
				);
				
			case ViewStates.Paintwork:

				// transition the camera to the relevant viewpoint.
				props.cameraController.changeCameraView(CameraViews.ModelView);

				// present the body paint view.
				return (
					<PaintworkView
						paintConfigurator={props.paintConfigurator}
						interiorConfigurator={props.interiorConfigurator}
						exteriorConfigurator={props.exteriorConfigurator}
						onBackButtonClicked={() => {
							handleChangeView(ViewStates.Home);
						}}
						onNextButtonClicked={() => {
							handleChangeView(ViewStates.Wheels);
						}}
					/>
				);

			case ViewStates.Wheels:

				// transition the camera to the relevant viewpoint.
				props.cameraController.changeCameraView(CameraViews.LeftWheelView);

				// present the wheels view.
				return (
					<WheelsView
						wheelConfigurator={props.wheelConfigurator}
						onBackButtonClicked={() => {
							handleChangeView(ViewStates.Paintwork);
						}}
						onNextButtonClicked={() => {
							handleChangeView(ViewStates.Interior);
						}}
					/>
				);

			case ViewStates.Interior:

				// present the interior view.
				return (
					<InteriorView
						cameraController={props.cameraController}
						interiorConfigurator={props.interiorConfigurator}
						onBackButtonClicked={() => {
							handleChangeView(ViewStates.Wheels);
						}}
						onNextButtonClicked={() => {
							handleChangeView(ViewStates.Exterior);
						}}
					/>
				);

			case ViewStates.Exterior:

				// present the exterior view.
				return (
					<ExteriorView
						cameraController={props.cameraController}
						exteriorConfigurator={props.exteriorConfigurator}
						onBackButtonClicked={() => {
							handleChangeView(ViewStates.Interior);
						}}
						onNextButtonClicked={() => {
							handleChangeView(ViewStates.Accessories);
						}}
					/>
				);

			case ViewStates.Accessories:

				// present the accessories view.
				return (
					<AccessoriesView
						accessoriesConfigurator={props.accessoriesConfigurator}
						cameraController={props.cameraController}
						onBackButtonClicked={() => {
							handleChangeView(ViewStates.Exterior);
						}}
						onNextButtonClicked={() => {
							handleChangeView(ViewStates.Summary);
						}}
					/>
				);

			case ViewStates.Summary:

				// present the summary view.
				return (
					<SummaryView
						cameraController={props.cameraController}
						onBackButtonClicked={() => {
							props.cameraController.changeCameraView(CameraViews.ModelView);
							handleChangeView(ViewStates.Accessories);
						}}
						startOnStepTwo={previousViewForRender === ViewStates.SendToDealer}
						isMobile={props.isMobile}
					/>
				);

			case ViewStates.SendToDealer:

				// present the save configuration view.
				return (
					<SendToDealerView
						cameraController={props.cameraController}
						onBackButtonClicked={() => {
							handleChangeView(previousViewState.current as ViewStates);
						}}
						showHeader={true}
						onComplete={() => handleChangeView(ViewStates.Summary)}
					/>
				);

			case ViewStates.ImportantInformation:

				// present the important information view.
				return (
					<ImportantInformationView
						onBackButtonClicked={() => {
							handleChangeView(previousViewState.current as ViewStates);
						}}
					/>
				);

			default:
				return null;
		}
	};

	const updateGlobalUserDetails = (key: string, value: string) => {
		setGlobalUserDetails(prevState => ({
			...prevState,
			[key]: value
		}));
	}

	// stop menu from showing in certain views.
	const shouldHideMenu = () => {
		return viewState === ViewStates.Home ||
			viewState === ViewStates.ImportantInformation ||
			viewState === ViewStates.LoadConfiguration ||
			viewState === ViewStates.SendToDealer;
	}
	
	// lower roof and doors, ready to take car images for PDF.
	const lowerRoofAndDoors = () => {
		props.exteriorConfigurator.selectSoftTopState(false);
		setHoodUp(false);

		props.exteriorConfigurator.selectHardTopState(false, false, true);
		setHardTopVisible(false);
		
		props.exteriorConfigurator.selectSideWindowState(false);
		setWindowsUp(false);
	}

	// global toggle hood.
	const toggleHood = () => {

		// only toggle hood up or down if hard top wasn't already selected otherwise just switch from hard top to soft top.
		const shouldToggleHoodUp = !hardTopVisible;

		let newHoodUp = hoodUp;
		if (shouldToggleHoodUp) {
			newHoodUp = !hoodUp;
		}
		props.exteriorConfigurator.selectSoftTopState(newHoodUp);
		setHoodUp(newHoodUp);

		setHardTopVisible(false);
	}

	// global toggle hard top.
	const toggleHardTop = () => {
		props.exteriorConfigurator.selectHardTopState(!hardTopVisible, hoodUp);
		setHardTopVisible(!hardTopVisible);
	}

	// on page load...
	useEffect(() => {
		function extractShortcode(urlString: string) {
			try {
				const url = new URL(urlString);
				return url.searchParams.get('shortcode');
			} catch (error) {
				console.error('Invalid URL:', error);
				return null;
			}
		}

		const loadConfiguration = async (localShortcode: string) => {
			await CloudData.loadConfiguration(localShortcode);
			handleConfigurationDataLoaded();
		}

		// set the page title, but only when running locally, so that the selected model is visible while developing.
		// the published sites have their title injected into index.html at publish time - see page-metadata/readme.md.
		if (window.location.hostname === 'localhost') {
			document.title = Configuration.pageTitle;
		}

		// if shortcode is in URL then extract it and load the configuration.
		const shortcode = extractShortcode(window.location.href);
		if (shortcode) {
			loadConfiguration(shortcode);
		}

		// run once on mount only, to read the shortcode from the URL; handleConfigurationDataLoaded is intentionally excluded from the deps.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// temporary debug function to allow cycling through camera views with the 'a' key, to check the different viewpoints.

	/*
	// selects the next camera view in the enum (for debugging), wrapping around to the start if currently at the end.
	const selectNextCameraView = () => {

		// get the total number of camera views (accounting for numeric enum double keys).
		const totalViews = Object.keys(CameraViews).length / 2;

		// get the current camera view index, and select the next one.
		const current = props.cameraController.currentView;
		if (current !== null) {
			const next = (current + 1) % totalViews;
			props.cameraController.changeCameraView(next);
			console.log('Camera view changed to:', CameraViews[next]);
		}
	};

	// key handler for debugging.
	useEffect(() => {
		const handleKey = (e: KeyboardEvent) => {

			// 'a' key switches to the next camera view.
			if (e.key === 'a') {
				e.preventDefault();
				selectNextCameraView();
			}
		};

		window.addEventListener('keydown', handleKey);
		return () => window.removeEventListener('keydown', handleKey);
	}, []);
	*/

	// return the HTML.
	return (
		<AppContext.Provider
			value={{
				viewState,
				handleChangeView,
				cameraController: props.cameraController,
				restartConfiguration,
				exteriorConfigurator: props.exteriorConfigurator,
				updateGlobalUserDetails,
				globalUserDetails,
				hoodUp,
				hardTopVisible,
				toggleHood,
				toggleHardTop,
				setHardTopVisible,
				windowsUp,
				setWindowsUp,
				lowerRoofAndDoors,
				isDrawerOpen,
				setIsDrawerOpen,
				isMobile: props.isMobile,
				applyConfiguration
			}}>
			{currentView()}
			{!shouldHideMenu() && (
				<React.Fragment>
					<VehicleMenu />

					{viewState !== ViewStates.Summary && (
						<img id = "desktop-logo" {...Configuration.logoProps()} />
					)}
					
					{previousViewForRender === ViewStates.Home && (
						<div className="tablet-modal-container">
							<Modal
								content={[{
									title: 'Welcome to the Beta Configurator',
									body: "This beta version is currently optimised for mobile devices. If you're seeing this message, please rotate your device to portrait to configure."
								}]}
								centreTitle={true}
							/>
						</div>
					)}
				</React.Fragment>
			)}
		</AppContext.Provider> 
	);
}

// context to allow any view in the hierarchy to call the change view handler, allowing the hamburger menu to work.
export const AppContext = createContext<AppContextType | undefined>(undefined);

export function showCanvas(shouldShow: boolean) {

	// add or remove the 'hide' class from the canvas element.
	const canvasElement = document.querySelector('#canvas') as HTMLElement;
	if (shouldShow) {
		canvasElement.classList.remove('hide');
	}
	else {
		canvasElement.classList.add('hide');
	}
}
